import type {
  UploadStatus,
  ProcessingStatus,
} from "src/reusable/types/fileUploadWithProgress";
import type { EnrichedArtifact } from "src/reusable/Chat/PromptInput/ArtifactSelectorModal";

/**
 * One input document of a workflow run, through its upload/processing life.
 */
export type WorkflowInputFile = {
  file?: File; // Optional - only present for newly uploaded files
  name: string;
  id?: number;
  size: number;
  datasetId?: number; // Dataset ID for tracking processing status
  sdrId?: number; // Source Data Record ID for matching processing status
  hookFileId?: string; // Internal file ID from useFileUploadWithProgress hook
  uploadProgress?: number;
  processingProgress?: number;
  uploadStatus?: UploadStatus;
  processingStatus?: ProcessingStatus;
  error?: string; // Failure message from the upload hook
  awaitingArtifact?: "finalizing" | "failed" | false; // Set when artifact ID fetch is pending or exhausted
};

export interface WorkflowRunCoreState {
  runId: number | undefined;
  dialogueId: number | undefined;
  dialogueCreationPromise: Promise<number> | null;
  inputFiles: WorkflowInputFile[];
  /** Input documents picked from existing datasets (vs uploaded fresh). */
  selectedArtifacts: EnrichedArtifact[];
  removeUploadedFile: ((fileId: string) => Promise<void>) | null;
}

export interface WorkflowRunCoreActions {
  setRunId: (id: number | undefined) => void;
  setDialogueId: (id: number | undefined) => void;
  setDialogueCreationPromise: (promise: Promise<number> | null) => void;
  getOrCreateDialogue: (createFn: () => Promise<number>) => Promise<number>;
  setInputFiles: (files: WorkflowInputFile[]) => void;
  addInputFile: (file: WorkflowInputFile) => void;
  removeInputFile: (artifactId: number) => void;
  setSelectedArtifacts: (artifacts: EnrichedArtifact[]) => void;
  removeSelectedArtifact: (artifactId: number) => void;
  setRemoveUploadedFileFunction: (
    fn: ((fileId: string) => Promise<void>) | null,
  ) => void;
}

export type WorkflowRunCore = WorkflowRunCoreState & WorkflowRunCoreActions;

/**
 * What shared components require of a product's store hook. Any zustand hook
 * built on the core slice satisfies this structurally, extras and all.
 */
export interface WorkflowRunStoreHandle {
  (): WorkflowRunCore;
  /** Zustand's selector form, so shared components subscribe to one field. */
  <T>(selector: (state: WorkflowRunCore) => T): T;
  getState: () => WorkflowRunCore;
}

export const workflowRunCoreInitialState: WorkflowRunCoreState = {
  runId: undefined,
  dialogueId: undefined,
  dialogueCreationPromise: null,
  inputFiles: [],
  selectedArtifacts: [],
  removeUploadedFile: null,
};

type CoreSet = (
  partial:
    | Partial<WorkflowRunCoreState>
    | ((state: WorkflowRunCoreState) => Partial<WorkflowRunCoreState>),
) => void;

/**
 * Zustand slice with the run state every workflow product shares: run and
 * dialogue ids, the dialogue-creation promise lock, and input-file upload
 * state. Each product composes it into its own store instance — one store
 * per product, never a shared keyed store, so state cannot leak between
 * products on navigation and reset semantics stay product-specific.
 *
 * The product's own `reset` must spread `workflowRunCoreInitialState` while
 * preserving `removeUploadedFile`: that field is a function registered by the
 * mounted uploader component, and clearing it on a data reset would leave the
 * store permanently null (resets run after the uploader's useEffect).
 */
export function createWorkflowRunCoreSlice(
  set: CoreSet,
  get: () => WorkflowRunCoreState,
): WorkflowRunCore {
  return {
    ...workflowRunCoreInitialState,
    setRunId: (id) => set({ runId: id }),
    setDialogueId: (id) => set({ dialogueId: id }),
    setDialogueCreationPromise: (promise) =>
      set({ dialogueCreationPromise: promise }),
    getOrCreateDialogue: async (createFn) => {
      const state = get();

      // If dialogue already exists, return it immediately
      if (state.dialogueId) {
        return state.dialogueId;
      }

      // If creation is already in progress, wait for it
      if (state.dialogueCreationPromise) {
        return state.dialogueCreationPromise;
      }

      // Start new dialogue creation
      const promise = createFn()
        .then((id) => {
          set({ dialogueId: id, dialogueCreationPromise: null });
          return id;
        })
        .catch((error) => {
          set({ dialogueCreationPromise: null });
          throw error;
        });

      set({ dialogueCreationPromise: promise });
      return promise;
    },
    setInputFiles: (files) => set({ inputFiles: files || [] }),
    addInputFile: (file) =>
      set((state) => ({ inputFiles: [...state.inputFiles, file] })),
    removeInputFile: (artifactId) =>
      set((state) => ({
        inputFiles: state.inputFiles.filter((f) => f.id !== artifactId),
      })),
    setSelectedArtifacts: (artifacts) =>
      set({ selectedArtifacts: artifacts || [] }),
    removeSelectedArtifact: (artifactId) =>
      set((state) => ({
        selectedArtifacts: state.selectedArtifacts.filter(
          (a) => a.id !== artifactId,
        ),
      })),
    setRemoveUploadedFileFunction: (fn) => set({ removeUploadedFile: fn }),
  };
}
